<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import type { ChatMessageSendingJobTarget_CsvTarget } from '../types'
import ButtonClose from '@/components/atoms/ButtonClose.vue'

const props = defineProps<{
  modelValue: ChatMessageSendingJobTarget_CsvTarget
}>()

const emit = defineEmits<{
  (
    event: 'update:modelValue',
    value: ChatMessageSendingJobTarget_CsvTarget
  ): void
}>()

type CsvInputId = string

type CsvInput = {
  id: CsvInputId
  fileName: string
  okanentClientForeignUserIds: string[]
}

const csvInputs = ref<Record<CsvInputId, CsvInput>>({})

const allOkanentClientForeignUserIds = computed(() => {
  const all = Object.entries(csvInputs.value).flatMap(
    ([, csvInput]) => csvInput.okanentClientForeignUserIds
  )
  return Array.from(new Set(all))
})

watch(allOkanentClientForeignUserIds, newValue => {
  emit('update:modelValue', {
    ...props.modelValue,
    okanentClientUserIds: newValue,
  })
})

const handleFileInput = async (e: Event) => {
  const target = e.target as HTMLInputElement
  if (target.files == undefined) {
    return
  }

  const files = Array.from(target.files)

  if (Object.keys(csvInputs.value).length + files.length > 5) {
    window.alert('ファイルは5個まで選択できます')
    return
  }

  // input の持つファイルをクリアしている
  // これをしないと以下の場合にこまる
  //   - A ファイルを選択する
  //   - dolphin 上からファイルを削除する
  //   - A ファイルを選択する
  // このとき、A ファイルが選択されたイベントが発火しない
  // なぜなら削除は Vue の状態の上での削除であり、input 要素で保持しているから
  // よって、処理をシンプルにするために、input 要素のファイルを毎回クリアする
  // ただしこのままでも Aファイルを選択 -> 再度 Aファイルを選択 したときも問題はない
  //   - ファイルに対して生成される CsvInputId が同じファイルからは同じ値が生成される
  //   - 入力した内容は csvInputs として Recrod で管理しているので、CsvInputId が同じなら上書きされる
  target.value = ''

  const appendedCsvInputs: CsvInput[] = []

  for (const file of files) {
    if (file.type !== 'text/csv') {
      window.alert('CSVファイルのみ選択できます')
      return
    }

    const content = await file.text()
    const okanentClientForeignUserIds = content
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean)

    if (okanentClientForeignUserIds.length > 10_000) {
      window.alert('1ファイルあたりのユーザーIDは10,000件までです')
      return
    }

    // eslint-disable-next-line fp/no-mutating-methods
    appendedCsvInputs.push({
      id: `${file.name}:::${file.lastModified}`,
      fileName: file.name,
      okanentClientForeignUserIds,
    })
  }

  for (const csvInput of appendedCsvInputs) {
    csvInputs.value[csvInput.id] = csvInput
  }
}

const handleClickDeleteSelectionButton = (id: CsvInputId) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [id]: _, ...filteredValue } = csvInputs.value
  csvInputs.value = filteredValue
}
</script>

<template>
  <section>
    <div :class="$style.box">
      <div :class="$style.header">
        <p :class="$style.title">選択したファイル</p>
        <label :class="$style.addFileInputLabel"
          >ファイルを追加する
          <input
            type="file"
            multiple
            accept="text/csv"
            @change="handleFileInput"
            :class="$style.addFileInput"
          />
        </label>
      </div>
      <div :class="$style.audienceListWrapper">
        <div v-if="Object.keys(csvInputs).length > 0">
          <ul :class="$style.audienceList">
            <li
              v-for="csvInput of csvInputs"
              :key="csvInput.id"
              :class="$style.audienceListItem"
            >
              <span>・{{ csvInput.fileName }}</span>
              <ButtonClose
                @click="() => handleClickDeleteSelectionButton(csvInput.id)"
              />
            </li>
          </ul>
          <p :class="$style.userCount">
            合計 {{ allOkanentClientForeignUserIds.length }}人
          </p>
        </div>
        <p v-else>未選択</p>
      </div>
    </div>
    <div :class="$style.note">
      ユーザーIDのみを含む CSV ファイルをアップロードしてください。<br />
      1つのファイルに含めることができるIDは1万件までです。<br />
      誤った形式のIDが含まれるファイルはアップロードできません。
    </div>
  </section>
</template>

<style module lang="scss">
.box {
  border: 1px solid $color-border-low-emphasis;
}

.header {
  display: flex;
  justify-content: space-between;
  background-color: $color-border-low-emphasis;
  padding: 8px 16px;
}

.title {
  font-weight: bold;
}

.addFileInputLabel {
  cursor: pointer;
}

.addFileInput {
  position: absolute !important;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
}

.audienceListWrapper {
  padding: 16px;
}

.audienceList {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.audienceListItem {
  display: flex;
  align-items: center;
  gap: 8px;
}

.userCount {
  margin-top: 16px;
}

.note {
  margin-top: 16px;
  font-size: 12px;
  color: $color-text-and-object-low-emphasis;
}
</style>
